import { usePage } from "@inertiajs/react"

import type {
  RejectionReasonOption,
  RejectionReasonOptions,
  SharedProps,
} from "@/types"

/**
 * OKS-952 — reads the `rejection_reason_options` Inertia shared prop.
 *
 * One hook rather than three inline `usePage()` reads, so the surfaces cannot
 * drift in how they index the map or in how they handle its absence.
 *
 * The prop is OMITTED (not null) on shells without a seated brand context, so
 * `Partial<SharedProps>` is the honest type — see `use-unread-messages-count`
 * for the same posture on another conditionally-shared key.
 */
export function useRejectionReasonOptions(): RejectionReasonOptions {
  const page = usePage<Partial<SharedProps>>()
  return page.props.rejection_reason_options ?? {}
}

/**
 * The option set for one `from_status`, or `undefined` when that status has no
 * taxonomy entry (`new` cannot be rejected) or the prop is absent.
 *
 * ⚠️ Index by `from_status`, never by gate. `reviewed` and
 * `pending_brand_validation` share the screened-out half of the taxonomy but
 * their code sets are disjoint by 6 and 2 entries, so a gate-keyed lookup would
 * offer 10 options where 6 are legal — and every gate-label assertion would
 * stay green while it happened.
 */
export function useRejectionReasonOptionsFor(
  fromStatus: string | null | undefined,
): RejectionReasonOption | undefined {
  const options = useRejectionReasonOptions()
  return fromStatus ? options[fromStatus] : undefined
}

/**
 * Codes legal for EVERY supplied status — the bulk surface's narrowing.
 *
 * 🔴 The caller decides whether a batch is refusable-with-a-code by testing
 * whether this result minus `other` is empty — NOT by comparing gates. Gate is
 * a copy concept and the picker is code-driven: a `reviewed` +
 * `pending_brand_validation` batch spans two gates yet shares four usable
 * codes, so a gate-equality test would deny it a perfectly good intersection,
 * and would change behaviour again the next time the label voices are
 * re-grouped.
 *
 * Order follows the first status's declaration order, which is the server's
 * taxonomy order and is meaningful (not alphabetical).
 *
 * Returns `[]` for an empty input — no statuses means no shared code, and the
 * caller renders no picker rather than an empty one.
 */
export function intersectRejectionReasonCodes(
  options: RejectionReasonOptions,
  statuses: readonly string[],
): string[] {
  const distinct = [...new Set(statuses)]
  if (distinct.length === 0) return []

  const lists = distinct.map((status) => options[status]?.codes ?? [])
  // A status absent from the map contributes an empty list, which correctly
  // collapses the intersection — a batch containing a row we have no taxonomy
  // for cannot share a code with anything.
  return lists.reduce<string[]>(
    (acc, codes) => acc.filter((code) => codes.includes(code)),
    lists[0] ?? [],
  )
}
